import React, { useState, useEffect } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import IconButton from '@mui/joy/IconButton';
import Button from '@mui/joy/Button';
import Switch from '@mui/joy/Switch';
import Chip from '@mui/joy/Chip';
import Divider from '@mui/joy/Divider';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { themeColors } from '../../styles/theme';

const TYPE_OPTIONS = [
  { value: 'string', label: 'STR' },
  { value: 'number', label: 'NUM' },
  { value: 'boolean', label: 'BOOL' },
  { value: 'enum', label: 'ENUM' },
  { value: 'array', label: 'ARR' },
  { value: 'object', label: 'OBJ' },
];

const ITEM_TYPES = [
  { value: 'string', label: 'STR' },
  { value: 'number', label: 'NUM' },
  { value: 'boolean', label: 'BOOL' },
  { value: 'object', label: 'OBJ' },
];

let _cnt = 0;
const uid = () => `sf${++_cnt}`;

function makeField(name = '', type = 'string') {
  return {
    id: uid(),
    name,
    type,
    description: '',
    required: false,
    default: '',
    enumValues: [],
    itemType: 'string',
    itemDescription: '',
    itemProperties: [],
    properties: [],
    expanded: true,
  };
}

// JSON Schema → internal fields
function schemaToFields(properties, required = []) {
  return Object.entries(properties || {}).map(([key, val]) => {
    const isEnum = Array.isArray(val.enum);
    const type = isEnum ? 'enum' : (val.type || 'string');
    const f = makeField(key, type);
    f.description = val.description || '';
    f.required = required.includes(key);
    if (isEnum) {
      f.enumValues = [...val.enum];
    } else if (type === 'string') {
      f.default = val.default !== undefined ? String(val.default) : '';
    } else if (type === 'array') {
      f.itemType = val.items?.type || 'string';
      f.itemDescription = val.items?.description || '';
      if (val.items?.type === 'object') {
        f.itemProperties = schemaToFields(val.items.properties || {}, val.items.required || []);
      }
    } else if (type === 'object') {
      f.properties = schemaToFields(val.properties || {}, val.required || []);
    }
    return f;
  });
}

// Internal field → JSON Schema entry
function fieldToEntry(f) {
  let entry;
  if (f.type === 'enum') {
    const vals = f.enumValues.filter(v => v.trim());
    entry = { type: 'string' };
    if (vals.length > 0) entry.enum = vals;
  } else if (f.type === 'array') {
    let items;
    if (f.itemType === 'object') {
      items = buildObjectSchema(f.itemProperties || []);
    } else {
      items = { type: f.itemType || 'string' };
    }
    if (f.itemDescription) items.description = f.itemDescription;
    entry = { type: 'array', items };
  } else if (f.type === 'object') {
    entry = buildObjectSchema(f.properties || []);
  } else {
    entry = { type: f.type };
    if (f.type === 'string' && f.default) entry.default = f.default;
  }
  if (f.description) entry.description = f.description;
  return entry;
}

// fields[] → { type: 'object', properties, required }
function buildObjectSchema(fields) {
  const properties = {};
  const required = [];
  for (const f of fields) {
    const key = f.name.trim();
    if (!key) continue;
    if (f.required) required.push(key);
    properties[key] = fieldToEntry(f);
  }
  const schema = { type: 'object', properties };
  if (required.length > 0) schema.required = required;
  return schema;
}

// Build the root schema
function buildSchema(fields, title) {
  const schema = buildObjectSchema(fields);
  if (title.trim()) schema.title = title.trim();
  return schema;
}

// ─── Enum Values Editor ────────────────────────────────────────────────────────

function EnumValuesEditor({ values, onChange }) {
  const [input, setInput] = useState('');

  const add = () => {
    const val = input.trim();
    if (!val || values.includes(val)) return;
    onChange([...values, val]);
    setInput('');
  };

  return (
    <Box>
      {values.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
          {values.map((val, idx) => (
            <Chip
              key={idx}
              size="sm"
              endDecorator={
                <IconButton
                  size="sm"
                  onClick={() => onChange(values.filter((_, i) => i !== idx))}
                  sx={{ '--IconButton-size': '18px', border: 'none', p: 0, color: 'inherit', opacity: 0.6, '&:hover': { opacity: 1, backgroundColor: 'transparent' } }}
                >
                  <CloseIcon sx={{ fontSize: 10 }} />
                </IconButton>
              }
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                backgroundColor: `${themeColors.primary[500]}10`,
                color: themeColors.primary[700],
                border: 'none',
                height: 24,
                pr: 0.25,
              }}
            >
              {val}
            </Chip>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        <Input
          size="sm"
          placeholder="Add value..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          sx={inputSx}
          slotProps={{ input: { style: { fontFamily: 'monospace', fontSize: '0.8125rem' } } }}
        />
        <IconButton
          size="sm"
          onClick={add}
          sx={{
            border: `1px solid ${themeColors.neutral[200]}`,
            borderRadius: 1,
            color: themeColors.primary[500],
            '&:hover': { backgroundColor: `${themeColors.primary[500]}10` },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

// ─── Shared input style ────────────────────────────────────────────────────────

const inputSx = {
  border: `1px solid ${themeColors.neutral[200]}`,
  borderRadius: 1,
  backgroundColor: 'white',
  '--Input-focusedThickness': '0px',
  '&:hover': { borderColor: themeColors.neutral[300] },
  '&:focus-within': { borderColor: themeColors.primary[400] },
};

const selectSx = {
  border: `1px solid ${themeColors.neutral[200]}`,
  borderRadius: 1,
  backgroundColor: 'white',
  '--Select-focusedThickness': '0px',
  '&:hover': { borderColor: themeColors.neutral[300] },
  '&:focus-within': { borderColor: themeColors.primary[400] },
};

// ─── Field Card ───────────────────────────────────────────────────────────────

function FieldCard({ field, onUpdate, onDelete, depth = 0 }) {
  const handleTypeChange = (_, newType) => {
    onUpdate({ ...field, type: newType });
  };

  return (
    <Box
      sx={{
        border: `1px solid ${depth === 0 ? themeColors.neutral[200] : themeColors.neutral[300]}`,
        borderRadius: 1.5,
        p: 1.5,
        mb: 1,
        ml: depth > 0 ? 2.5 : 0,
        backgroundColor: depth === 0 ? 'white' : themeColors.neutral[50],
      }}
    >
      {/* Row 1: expand + name + type + required + delete */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {field.type === 'object' ? (
          <IconButton
            size="sm"
            onClick={() => onUpdate({ ...field, expanded: !field.expanded })}
            sx={{ width: 22, height: 22, minHeight: 22, p: 0, border: 'none', color: themeColors.neutral[500], flexShrink: 0, '&:hover': { backgroundColor: themeColors.neutral[100] } }}
          >
            {field.expanded ? <KeyboardArrowDownIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        ) : (
          <Box sx={{ width: 22, flexShrink: 0 }} />
        )}

        <Input
          size="sm"
          placeholder="field_name"
          value={field.name}
          onChange={(e) => onUpdate({ ...field, name: e.target.value })}
          sx={{ ...inputSx, flex: 1, fontFamily: 'monospace', fontSize: '0.8125rem', minWidth: 0 }}
        />

        <Select
          size="sm"
          value={field.type}
          onChange={handleTypeChange}
          sx={{ ...selectSx, width: 80, fontFamily: 'monospace', fontSize: '0.75rem', flexShrink: 0 }}
        >
          {TYPE_OPTIONS.map(t => (
            <Option key={t.value} value={t.value} sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
              {t.label}
            </Option>
          ))}
        </Select>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Typography level="body-xs" sx={{ fontSize: '0.6875rem', color: themeColors.neutral[500] }}>
            Req
          </Typography>
          <Switch
            size="sm"
            checked={!!field.required}
            onChange={(e) => onUpdate({ ...field, required: e.target.checked })}
            sx={{ '--Switch-trackWidth': '28px', '--Switch-trackHeight': '16px', '--Switch-thumbSize': '12px' }}
          />
        </Box>

        <IconButton
          size="sm"
          onClick={onDelete}
          sx={{
            width: 26, height: 26, minHeight: 26, border: 'none', flexShrink: 0,
            color: themeColors.neutral[400],
            '&:hover': { color: themeColors.danger, backgroundColor: `${themeColors.danger}10` },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {/* Row 2: description */}
      <Box sx={{ ml: '30px', mt: 1 }}>
        <Input
          size="sm"
          placeholder="Description..."
          value={field.description || ''}
          onChange={(e) => onUpdate({ ...field, description: e.target.value })}
          sx={{ ...inputSx, fontSize: '0.8125rem' }}
        />
      </Box>

      {/* String: default */}
      {field.type === 'string' && (
        <Box sx={{ ml: '30px', mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography level="body-xs" sx={{ fontSize: '0.6875rem', color: themeColors.neutral[500], flexShrink: 0 }}>
            Default:
          </Typography>
          <Input
            size="sm"
            placeholder='""'
            value={field.default || ''}
            onChange={(e) => onUpdate({ ...field, default: e.target.value })}
            sx={{ ...inputSx, flex: 1, fontFamily: 'monospace', fontSize: '0.8125rem' }}
          />
        </Box>
      )}

      {/* Enum: values */}
      {field.type === 'enum' && (
        <Box sx={{ ml: '30px', mt: 1 }}>
          <EnumValuesEditor
            values={field.enumValues || []}
            onChange={(vals) => onUpdate({ ...field, enumValues: vals })}
          />
        </Box>
      )}

      {/* Array: items config */}
      {field.type === 'array' && (
        <Box sx={{ ml: '30px', mt: 0.75, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography level="body-xs" sx={{ fontSize: '0.6875rem', color: themeColors.neutral[500], flexShrink: 0 }}>
            Items:
          </Typography>
          <Select
            size="sm"
            value={field.itemType || 'string'}
            onChange={(_, v) => onUpdate({ ...field, itemType: v })}
            sx={{ ...selectSx, width: 80, fontFamily: 'monospace', fontSize: '0.75rem', flexShrink: 0 }}
          >
            {ITEM_TYPES.map(t => (
              <Option key={t.value} value={t.value} sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                {t.label}
              </Option>
            ))}
          </Select>
          <Input
            size="sm"
            placeholder="Items description..."
            value={field.itemDescription || ''}
            onChange={(e) => onUpdate({ ...field, itemDescription: e.target.value })}
            sx={{ ...inputSx, flex: 1, fontSize: '0.8125rem' }}
          />
        </Box>
      )}

      {/* Array of objects: item sub-fields */}
      {field.type === 'array' && field.itemType === 'object' && (
        <Box sx={{ ml: '30px', mt: 1 }}>
          <Typography level="body-xs" sx={{ fontSize: '0.6875rem', color: themeColors.neutral[500], mb: 0.75 }}>
            Each item's fields:
          </Typography>
          {(field.itemProperties || []).map((sub, idx) => (
            <FieldCard
              key={sub.id}
              field={sub}
              depth={depth + 1}
              onUpdate={(updated) => {
                const next = [...field.itemProperties];
                next[idx] = updated;
                onUpdate({ ...field, itemProperties: next });
              }}
              onDelete={() => {
                onUpdate({ ...field, itemProperties: field.itemProperties.filter((_, i) => i !== idx) });
              }}
            />
          ))}
          {depth < 1 && (
            <Button
              size="sm"
              variant="plain"
              startDecorator={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={() => onUpdate({ ...field, itemProperties: [...(field.itemProperties || []), makeField()] })}
              sx={{
                color: themeColors.primary[500], fontWeight: 500, fontSize: '0.75rem',
                px: 1, py: 0.5, mt: 0.25, border: 'none',
                '&:hover': { backgroundColor: `${themeColors.primary[500]}08`, color: themeColors.primary[700] },
              }}
            >
              Add item field
            </Button>
          )}
        </Box>
      )}

      {/* Object: sub-fields */}
      {field.type === 'object' && field.expanded && (
        <Box sx={{ ml: '30px', mt: 1.5 }}>
          {(field.properties || []).map((sub, idx) => (
            <FieldCard
              key={sub.id}
              field={sub}
              depth={depth + 1}
              onUpdate={(updated) => {
                const next = [...field.properties];
                next[idx] = updated;
                onUpdate({ ...field, properties: next });
              }}
              onDelete={() => {
                onUpdate({ ...field, properties: field.properties.filter((_, i) => i !== idx) });
              }}
            />
          ))}
          {depth < 1 && (
            <Button
              size="sm"
              variant="plain"
              startDecorator={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={() => onUpdate({ ...field, properties: [...(field.properties || []), makeField()] })}
              sx={{
                color: themeColors.primary[500], fontWeight: 500, fontSize: '0.75rem',
                px: 1, py: 0.5, mt: 0.25, border: 'none',
                '&:hover': { backgroundColor: `${themeColors.primary[500]}08`, color: themeColors.primary[700] },
              }}
            >
              Add sub-field
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export default function SchemaEditorModal({ open, onClose, schema, onSave }) {
  const [title, setTitle] = useState('');
  const [fields, setFields] = useState([]);
  const [mode, setMode] = useState('visual'); // 'visual' | 'json'
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');

  useEffect(() => {
    if (!open) return;
    setMode('visual');
    setJsonError('');
    if (schema) {
      setTitle(schema.title || '');
      setFields(schemaToFields(schema.properties || {}, schema.required || []));
      setJsonText(JSON.stringify(schema, null, 2));
    } else {
      setTitle('');
      setFields([]);
      setJsonText('');
    }
  }, [open]);

  // Switch Visual → JSON: serialize the current visual state to text.
  const goToJson = () => {
    setJsonText(JSON.stringify(buildSchema(fields, title), null, 2));
    setJsonError('');
    setMode('json');
  };

  // Switch JSON → Visual: parse text back into fields (stay on JSON if invalid).
  const goToVisual = () => {
    try {
      const parsed = JSON.parse(jsonText || '{}');
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Schema must be a JSON object');
      }
      setTitle(parsed.title || '');
      setFields(schemaToFields(parsed.properties || {}, parsed.required || []));
      setJsonError('');
      setMode('visual');
    } catch (e) {
      setJsonError(e.message);
    }
  };

  const handleSave = () => {
    if (mode === 'json') {
      try {
        const parsed = JSON.parse(jsonText || '{}');
        if (typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Schema must be a JSON object');
        }
        onSave(parsed);
      } catch (e) {
        setJsonError(e.message);
      }
      return;
    }
    onSave(buildSchema(fields, title));
  };

  const updateField = (idx, updated) => {
    const next = [...fields];
    next[idx] = updated;
    setFields(next);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          p: 0,
          maxWidth: 600,
          width: '95vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          border: `1px solid ${themeColors.neutral[200]}`,
          boxShadow: '0 24px 48px -8px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3, py: 2.5,
            borderBottom: `1px solid ${themeColors.neutral[100]}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
            backgroundColor: themeColors.neutral[50],
          }}
        >
          <Box>
            <Typography level="title-md" sx={{ fontWeight: 700, color: themeColors.neutral[900] }}>
              Output Schema
            </Typography>
            <Typography level="body-xs" sx={{ color: themeColors.neutral[500], mt: 0.25 }}>
              OpenAI-compatible JSON Schema
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Visual / JSON toggle */}
            <Box sx={{ display: 'flex', border: `1px solid ${themeColors.neutral[200]}`, borderRadius: 1.5, overflow: 'hidden' }}>
              {['visual', 'json'].map((m) => (
                <Box
                  key={m}
                  onClick={() => { if (m === mode) return; m === 'json' ? goToJson() : goToVisual(); }}
                  sx={{
                    px: 1.5, py: 0.5, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.4px',
                    backgroundColor: mode === m ? themeColors.primary[500] : 'white',
                    color: mode === m ? 'white' : themeColors.neutral[500],
                    '&:hover': { backgroundColor: mode === m ? themeColors.primary[500] : themeColors.neutral[100] },
                  }}
                >
                  {m === 'visual' ? 'Visual' : 'JSON'}
                </Box>
              ))}
            </Box>
            <ModalClose
              variant="plain"
              sx={{ position: 'static', top: 'unset', right: 'unset', '--IconButton-size': '32px' }}
            />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2.5 }}>
          {mode === 'json' ? (
            <Box>
              <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.75, color: themeColors.neutral[600], fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Schema JSON
              </Typography>
              <Textarea
                minRows={16}
                value={jsonText}
                onChange={(e) => { setJsonText(e.target.value); if (jsonError) setJsonError(''); }}
                placeholder='{ "title": "my_schema", "type": "object", "properties": { … } }'
                sx={{
                  ...inputSx,
                  fontFamily: 'monospace', fontSize: '0.8125rem', lineHeight: 1.6,
                  '& textarea': { whiteSpace: 'pre' },
                }}
              />
              {jsonError && (
                <Typography level="body-xs" sx={{ mt: 1, color: themeColors.danger, fontFamily: 'monospace' }}>
                  Invalid JSON: {jsonError}
                </Typography>
              )}
              <Typography level="body-xs" sx={{ mt: 1, color: themeColors.neutral[500] }}>
                Paste any valid JSON Schema (nested objects and arrays of objects are supported).
                required / additionalProperties are added automatically.
              </Typography>
            </Box>
          ) : (
          <>
          {/* Schema title */}
          <Box sx={{ mb: 2.5 }}>
            <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.75, color: themeColors.neutral[600], fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Schema Title
            </Typography>
            <Input
              size="sm"
              placeholder="my_schema"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ ...inputSx, fontFamily: 'monospace', fontSize: '0.875rem' }}
            />
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          {/* Fields */}
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography level="body-xs" sx={{ fontWeight: 600, color: themeColors.neutral[600], fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Fields
            </Typography>
            {fields.length > 0 && (
              <Typography level="body-xs" sx={{ color: themeColors.neutral[400], fontSize: '0.6875rem' }}>
                {fields.length} field{fields.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          {fields.length === 0 && (
            <Box
              sx={{
                textAlign: 'center', py: 4,
                border: `1px dashed ${themeColors.neutral[300]}`,
                borderRadius: 1.5,
                color: themeColors.neutral[400],
                mb: 1.5,
              }}
            >
              <Typography level="body-sm" sx={{ color: themeColors.neutral[400] }}>
                No fields yet. Add one below.
              </Typography>
            </Box>
          )}

          {fields.map((field, idx) => (
            <FieldCard
              key={field.id}
              field={field}
              depth={0}
              onUpdate={(updated) => updateField(idx, updated)}
              onDelete={() => setFields(fields.filter((_, i) => i !== idx))}
            />
          ))}

          <Button
            size="sm"
            variant="plain"
            startDecorator={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={() => setFields([...fields, makeField()])}
            sx={{
              mt: 0.5,
              color: themeColors.primary[500],
              fontWeight: 600,
              fontSize: '0.8125rem',
              border: `1px dashed ${themeColors.primary[300]}`,
              borderRadius: 1.5,
              width: '100%',
              py: 1,
              '&:hover': { backgroundColor: `${themeColors.primary[500]}08`, borderColor: themeColors.primary[400], color: themeColors.primary[700] },
            }}
          >
            Add field
          </Button>
          </>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3, py: 2,
            borderTop: `1px solid ${themeColors.neutral[100]}`,
            display: 'flex', gap: 1, justifyContent: 'flex-end',
            flexShrink: 0,
            backgroundColor: themeColors.neutral[50],
          }}
        >
          <Button
            variant="plain"
            size="sm"
            onClick={onClose}
            sx={{ color: themeColors.neutral[600], border: 'none', '&:hover': { backgroundColor: themeColors.neutral[100] } }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            sx={{
              backgroundColor: themeColors.primary[500],
              color: 'white',
              fontWeight: 600,
              border: 'none',
              '&:hover': { backgroundColor: themeColors.primary[600] },
            }}
          >
            Save Schema
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
